/**
 * Classificação dos erros da consulta de CRM no CFM (`GET /cfm`, via
 * `Network.cfm.validateCrm`). O CFM é serviço EXTERNO (SOAP chamado pelo
 * backend) e o seu modo de falha tem shape próprio — por isso a tradução mora
 * aqui, e não em `apiError.ts` (rule `error-handling.md`). Consumidor:
 * cadastro público do médico (`SignUp/doctor/steps/ProfessionalData.vue`).
 *
 * Contrato observado no backend (`modules/cfm/domain/cfm.service.ts#showCRM` e
 * `services/external/cfm/cfm.service.ts#searchCrm`):
 * - 404 `'CRM não encontrado'` — o CFM respondeu e o CRM não existe;
 * - 400 com `message` string `'CRM já vinculado a um médico'` — CRM de um
 *   cadastro que já tem login;
 * - 400 com `errorCode: 'VALIDATION_ERROR'` — parâmetros inválidos (DTO);
 * - 400 SEM `message` — a chamada ao CFM falhou (indisponível, timeout do SOAP,
 *   cliente não inicializado): o backend lança `BadRequestException({ error })`
 *   e o body só carrega `{ error, statusCode }`.
 *
 * Sem este discriminador o 400 do CFM fora do ar era exibido como "CRM já em
 * uso" — e o médico ficava travado no cadastro sem saber que bastava esperar.
 */
import {
  getApiErrorMessage,
  getApiErrorStatus,
  hasApiErrorCode,
  isNetworkError,
  isTimeoutError,
  unwrapApiError
} from '@/utils/apiError'

/** Código do `validationExceptionFactory` do backend (DTO inválido). */
export const CFM_VALIDATION_ERROR_CODE = 'VALIDATION_ERROR'

/**
 * Orientação quando o CFM não pôde ser consultado: distinta de "CRM não
 * encontrado" — o CRM pode existir, só não foi possível confirmar agora.
 */
export const CFM_UNAVAILABLE_MESSAGE =
  'Não foi possível consultar o CFM no momento. Aguarde alguns minutos e tente novamente; ' +
  'se o problema continuar, entre em contato com o suporte.'

export type CfmLookupFailure = 'not_found' | 'already_linked' | 'invalid_input' | 'unavailable'

type CfmErrorLike = {
  response?: { data?: { message?: unknown } }
}

function hasBackendMessage(error: unknown): boolean {
  // eslint-disable-next-line no-restricted-syntax -- classificador do CFM (shape externo), erro já desembrulhado
  const message = (unwrapApiError(error) as CfmErrorLike | null | undefined)?.response?.data
    ?.message
  return typeof message === 'string' && message.trim() !== ''
}

/**
 * Classifica a falha da consulta ao CFM. Aceita o erro cru do axios ou o
 * `ServiceError` do `CfmService` (o status/body vêm do erro embrulhado).
 * Qualquer caso não reconhecido (5xx, rede, timeout, 400 sem mensagem) é
 * tratado como CFM indisponível — nunca como "CRM inexistente" ou "já em uso".
 */
export function getCfmLookupFailure(error: unknown): CfmLookupFailure {
  const status = getApiErrorStatus(error)
  if (status === 404) return 'not_found'
  if (status === 400) {
    if (hasApiErrorCode(error, CFM_VALIDATION_ERROR_CODE)) return 'invalid_input'
    if (hasBackendMessage(error)) return 'already_linked'
  }
  return 'unavailable'
}

/**
 * Mensagem do caso `unavailable`: sem conexão/timeout do lado do usuário usam
 * as mensagens canônicas (o problema é a internet dele, não o CFM); todo o
 * resto vira a orientação de CFM indisponível — inclusive 5xx, que o funil
 * genérico rebaixaria para "Não foi possível concluir".
 */
export function getCfmUnavailableMessage(error: unknown): string {
  if (isNetworkError(error) || isTimeoutError(error)) {
    return getApiErrorMessage(unwrapApiError(error), CFM_UNAVAILABLE_MESSAGE)
  }
  return CFM_UNAVAILABLE_MESSAGE
}
